import { useState, useCallback, useEffect } from 'react';
import type { ControlDefinition, ControlValues } from '@/components/visuals';

/**
 * Typed accessor for control values. Use in renderFocusChart and elsewhere instead of
 * reading values[key] as unknown. All consumers should import this from here.
 */
export function getControlValue<K extends keyof ControlValues>(
  values: Partial<ControlValues>,
  key: K,
  fallback: ControlValues[K]
): ControlValues[K] {
  const v = values[key];
  if (v === undefined) return fallback;
  return v as ControlValues[K];
}

function buildDefaults(controls: ControlDefinition[] | undefined): Partial<ControlValues> {
  if (!controls?.length) return {};
  const out: Partial<ControlValues> = {};
  for (const c of controls) {
    if (c.type === 'search') out['search'] = '';
    else if (c.type === 'show-count') out['show-count'] = c.default;
    else if (c.type === 'sort') out['sort'] = c.default;
    else if (c.type === 'include-negatives') out['include-negatives'] = c.default;
    else if (c.type === 'show-labels') out['show-labels'] = c.default;
    else if (c.type === 'show-baseline') out['show-baseline'] = c.default;
    else if (c.type === 'schedule-date-basis') out['schedule-date-basis'] = c.default;
    else if (c.type === 'include-milestones') out['include-milestones'] = c.default;
    else if (c.type === 'units-spread-basis') out['units-spread-basis'] = c.default;
  }
  return out;
}

/**
 * Hook for focus-mode chart controls. Initializes state from config defaults.
 * When the focus chart changes, key the focus view by focusChart.id so this hook
 * runs in a fresh component and state resets (no need for reset logic here).
 */
export function useChartControls(
  controls: ControlDefinition[] | undefined,
  chartId: string | undefined
): {
  values: Partial<ControlValues>;
  handleChange: (type: ControlDefinition['type'], value: unknown) => void;
} {
  const [values, setValues] = useState<Partial<ControlValues>>(() =>
    buildDefaults(controls)
  );

  useEffect(() => {
    setValues(buildDefaults(controls));
  }, [chartId, controls]);

  const handleChange = useCallback((type: ControlDefinition['type'], value: unknown) => {
    setValues((prev) => ({ ...prev, [type]: value }));
  }, []);

  if (!controls?.length) {
    return { values: {}, handleChange: () => {} };
  }

  return { values, handleChange };
}
